import { useState, useEffect } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Bell, Building2, ShieldCheck, Users, Loader2 } from 'lucide-react';

interface NotificationPreference {
  id?: string;
  user_id: string;
  agency_created: boolean;
  agency_approved: boolean;
  agency_suspended: boolean;
  agency_deleted: boolean;
  supervisor_added: boolean;
  supervisor_removed: boolean;
  patient_registered: boolean;
  email_enabled: boolean;
}

const defaultPreferences: Omit<NotificationPreference, 'user_id'> = {
  agency_created: true,
  agency_approved: true,
  agency_suspended: true,
  agency_deleted: true,
  supervisor_added: true,
  supervisor_removed: true,
  patient_registered: true,
  email_enabled: true,
};

export function NotificationPreferences() {
  const { user } = useAdminAuth();
  const [preferences, setPreferences] = useState<NotificationPreference | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;

    const fetchPreferences = async () => {
      const { data, error } = await supabase
        .from('admin_notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching preferences:', error);
        toast.error('Failed to load notification preferences');
      } else if (data) {
        setPreferences(data as NotificationPreference);
      } else {
        // No preferences exist yet, use defaults
        setPreferences({ user_id: user.id, ...defaultPreferences });
      }
      setLoading(false);
    };

    fetchPreferences();
  }, [user]);

  const handleToggle = (key: keyof Omit<NotificationPreference, 'id' | 'user_id'>) => {
    if (!preferences) return;
    setPreferences({ ...preferences, [key]: !preferences[key] });
  };

  const handleSave = async () => {
    if (!preferences || !user) return;

    setSaving(true);
    try {
      if (preferences.id) {
        // Update existing
        const { error } = await supabase
          .from('admin_notification_preferences')
          .update({
            agency_created: preferences.agency_created,
            agency_approved: preferences.agency_approved,
            agency_suspended: preferences.agency_suspended,
            agency_deleted: preferences.agency_deleted,
            supervisor_added: preferences.supervisor_added,
            supervisor_removed: preferences.supervisor_removed,
            patient_registered: preferences.patient_registered,
            email_enabled: preferences.email_enabled,
            updated_at: new Date().toISOString(),
          })
          .eq('id', preferences.id);

        if (error) throw error;
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('admin_notification_preferences')
          .insert({
            user_id: user.id,
            agency_created: preferences.agency_created,
            agency_approved: preferences.agency_approved,
            agency_suspended: preferences.agency_suspended,
            agency_deleted: preferences.agency_deleted,
            supervisor_added: preferences.supervisor_added,
            supervisor_removed: preferences.supervisor_removed,
            patient_registered: preferences.patient_registered,
            email_enabled: preferences.email_enabled,
          })
          .select()
          .single();

        if (error) throw error;
        setPreferences(data as NotificationPreference);
      }

      toast.success('Notification preferences saved');
    } catch (error: any) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!preferences) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <CardTitle>Email Notifications</CardTitle>
        </div>
        <CardDescription>
          Choose which activities trigger email alerts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Master toggle */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-accent/50">
          <div className="space-y-0.5">
            <Label htmlFor="email_enabled" className="text-base font-medium">
              Email Notifications
            </Label>
            <p className="text-sm text-muted-foreground">
              Enable or disable all email notifications
            </p>
          </div>
          <Switch
            id="email_enabled"
            checked={preferences.email_enabled}
            onCheckedChange={() => handleToggle('email_enabled')}
          />
        </div>

        <Separator />

        {/* Agency notifications */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Building2 className="h-4 w-4" />
            <span>Agency Activity</span>
          </div>
          
          <div className="space-y-3 pl-6">
            <div className="flex items-center justify-between">
              <Label htmlFor="agency_created" className="font-normal">
                New agency registrations
              </Label>
              <Switch
                id="agency_created"
                checked={preferences.agency_created}
                onCheckedChange={() => handleToggle('agency_created')}
                disabled={!preferences.email_enabled}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="agency_approved" className="font-normal">
                Agency approvals
              </Label>
              <Switch
                id="agency_approved"
                checked={preferences.agency_approved}
                onCheckedChange={() => handleToggle('agency_approved')}
                disabled={!preferences.email_enabled}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="agency_suspended" className="font-normal">
                Agency suspensions
              </Label>
              <Switch
                id="agency_suspended"
                checked={preferences.agency_suspended}
                onCheckedChange={() => handleToggle('agency_suspended')}
                disabled={!preferences.email_enabled}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="agency_deleted" className="font-normal">
                Agency deletions
              </Label>
              <Switch
                id="agency_deleted"
                checked={preferences.agency_deleted}
                onCheckedChange={() => handleToggle('agency_deleted')}
                disabled={!preferences.email_enabled}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Staff notifications */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <ShieldCheck className="h-4 w-4" />
            <span>Staff Activity</span>
          </div>
          
          <div className="space-y-3 pl-6">
            <div className="flex items-center justify-between">
              <Label htmlFor="supervisor_added" className="font-normal">
                Supervisor added
              </Label>
              <Switch
                id="supervisor_added"
                checked={preferences.supervisor_added}
                onCheckedChange={() => handleToggle('supervisor_added')}
                disabled={!preferences.email_enabled}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="supervisor_removed" className="font-normal">
                Supervisor removed
              </Label>
              <Switch
                id="supervisor_removed"
                checked={preferences.supervisor_removed}
                onCheckedChange={() => handleToggle('supervisor_removed')}
                disabled={!preferences.email_enabled}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Patient notifications */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>Patient Activity</span>
          </div>
          
          <div className="space-y-3 pl-6">
            <div className="flex items-center justify-between">
              <Label htmlFor="patient_registered" className="font-normal">
                New patient registrations
              </Label>
              <Switch
                id="patient_registered"
                checked={preferences.patient_registered}
                onCheckedChange={() => handleToggle('patient_registered')}
                disabled={!preferences.email_enabled}
              />
            </div>
          </div>
        </div>

        <Separator />

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Preferences
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
